/**
 * @Author: Q in
 * @Date: 2026-03-11 15:00:00
 * @LastEditors: Q in
 * @LastEditTime: 2026-05-15 15:00:00
 * @FilePath: /new_kefu_pc/src/utils/index-websocket.js
 * @Description: 客户端 WebSocket 服务 - 支持自动重连、心跳检测、消息分发、状态监听、休眠恢复
 *
 * 架构说明：
 * - SimpleWebSocket 类：封装 WebSocket 连接、心跳、重连、消息收发等核心功能
 * - 单例管理：通过模块级 wsInstances 对象存储全局单例，支持多模块独立连接
 * - 后台保活：使用 Web Worker 解决浏览器后台标签页定时器被节流的问题
 * - 心跳机制：前台 28 秒间隔，后台 10 秒间隔（Worker 驱动），超时自动断开重连
 * - 休眠恢复：页面从后台恢复时主动探测连接状态，避免 onclose 事件被节流导致的状态误判
 */

// ─── 配置常量 ──────────────────────────────────────────────────────────────────

/**
 * WebSocket 连接全局配置
 * 所有时间单位为毫秒
 */
const CONFIG = {
	MAX_RECONNECT_COUNT: 10, // 最大重连次数，超过后暂停一段时间再重置（避免永久放弃）
	RECONNECT_DELAY: 3000, // 重连基础延迟（毫秒），实际延迟会随次数递增（最多 3 倍）
	HEARTBEAT_DELAY: 28000, // 前台心跳发送间隔（28 秒），页面可见时的正常心跳频率
	BACKGROUND_HEARTBEAT_DELAY: 10000, // 后台心跳发送间隔（10 秒），页面隐藏时更频繁发送防止断连
	HEARTBEAT_TIMEOUT: 60000, // 前台心跳超时时间（60 秒），超过此时间未收到 pong 则判定断开
	BACKGROUND_HEARTBEAT_TIMEOUT: 120000, // 后台心跳超时时间（120 秒），更宽容避免后台误断
	HEARTBEAT_MESSAGE: { a: 'ping', c: 'Message' }, // 心跳包内容，a 字段标识动作，c 字段标识类型
	HEARTBEAT_RESPONSE: 'pong', // 服务端心跳响应标识
	VISIBILITY_RECONNECT_DELAY: 500, // 页面恢复可见后的重连延迟（毫秒），给浏览器恢复时间
	BACKGROUND_HEALTH_CHECK_INTERVAL: 30000, // 后台健康检查间隔（30 秒），用于定期检查连接是否有效
};

// ─── SimpleWebSocket 类 ──────────────────────────────────────────────────────

/**
 * WebSocket 客户端封装类
 *
 * 核心功能：
 * 1. 连接管理：建立连接、关闭连接、更新 URL、手动重连
 * 2. 心跳保活：前台使用 setInterval，后台使用 Web Worker（避免浏览器节流）
 * 3. 心跳超时检测：超过指定时间未收到 pong 响应则主动断开重连
 * 4. 自动重连：指数退避策略，最多重连 10 次，之后等待 30 秒重置
 * 5. 消息分发：基于消息 type 的多路分发，支持 default 兜底处理器
 * 6. 状态监听：连接状态变化时通知所有注册的监听器
 * 7. 休眠恢复：页面从后台恢复时主动探测连接，不依赖可能被节流的 onclose 事件
 */
class SimpleWebSocket {
	/**
	 * 构造函数 - 初始化 WebSocket 实例但不立即连接
	 *
	 * @param {string} url WebSocket 服务器地址（ws:// 或 wss://）
	 */
	constructor(url) {
		// ========== 连接参数 ==========
		this.url = url; // WebSocket 服务器地址
		this.ws = null; // 原生 WebSocket 实例
		this.connected = false; // 是否已连接（onopen 触发后为 true）
		this.isConnecting = false; // 是否正在连接中（防止重复连接）

		// ========== 重连参数 ==========
		this.reconnectCount = 0; // 当前重连次数（连接成功后清零）
		this.maxReconnectCount = CONFIG.MAX_RECONNECT_COUNT; // 最大重连次数（10 次）
		this.reconnectDelay = CONFIG.RECONNECT_DELAY; // 重连基础延迟（3 秒）

		// ========== 心跳相关 ==========
		this.heartbeatTimer = null; // 前台心跳发送定时器 ID（setInterval）
		this.heartbeatTimeoutTimer = null; // 心跳超时检测定时器 ID（setTimeout）
		this.reconnectTimer = null; // 重连延迟定时器 ID（setTimeout）
		this.lastHeartbeatResponseTime = 0; // 最后一次收到心跳响应的时间戳（毫秒）

		// ========== 状态标记 ==========
		this.isManualClose = false; // 是否为手动关闭（手动关闭不触发自动重连）
		this.isBackground = false; // 页面是否在后台（隐藏状态）

		// ========== 后台保活相关 ==========
		this.visibilityChangeHandler = null; // 页面可见性变化处理函数引用（用于清理）
		this.heartbeatWorker = null; // Web Worker 实例（后台心跳，不受浏览器节流影响）
		this.workerUrl = null; // Worker Blob URL（用于释放内存）
		this.backgroundHealthCheckTimer = null; // 后台健康检查定时器

		// ========== 消息处理器注册表 ==========
		// key: 消息类型字符串（如 'chat_record'、'get_session_info'）
		// value: Set<function> - 同一类型可注册多个处理器，使用 Set 去重
		this.messageHandlers = new Map();

		// ========== 连接状态变化处理器 ==========
		// 存储所有关注连接状态变化的回调函数
		this.statusChangeHandlers = new Set();

		// 绑定页面可见性变化监听
		this._bindVisibilityListener();
	}

	// ─── 页面可见性监听 ──────────────────────────────────────────────────────

	/**
	 * 绑定页面可见性变化监听
	 *
	 * 监听的事件：
	 * 1. visibilitychange - 页面可见性变化（切换标签页、最小化窗口）
	 * 2. freeze/resume - 页面冻结/恢复（部分浏览器支持）
	 * 3. online/offline - 网络连接/断开
	 *
	 * 核心策略：
	 * - 页面进入后台：切换到 Web Worker 心跳（避免 setInterval 被浏览器节流）
	 * - 页面恢复前台：停止 Worker 心跳，主动探测连接状态，必要时重连
	 * - 网络恢复：自动重连
	 * - 网络断开：停止心跳，等待网络恢复
	 */
	_bindVisibilityListener() {
		// 清理旧的监听器（避免重复绑定）
		if (this.visibilityChangeHandler) {
			document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
		}

		this.visibilityChangeHandler = () => {
			if (!document.hidden) {
				// ========== 页面从隐藏变为可见 ==========
				console.log('[WS] 页面恢复可见，检测连接状态');
				this.isBackground = false;
				this._handleVisibilityRestore(); // 探测连接并决定是否重连
			} else {
				// ========== 页面变为隐藏 ==========
				// 浏览器会对后台标签页的 setInterval/setTimeout 进行节流（降至 1 次/分钟甚至更低）
				// 导致心跳无法按时发送，连接被服务端断开
				// 使用 Web Worker 不受节流影响，保持后台连接不断开
				console.log('[WS] 页面进入后台，切换到 Worker 心跳保持连接');
				this.isBackground = true;
				this._stopHeartbeat(); // 停止前台心跳
				this._startWorkerHeartbeat(); // 启动 Worker 心跳
			}
		};

		document.addEventListener('visibilitychange', this.visibilityChangeHandler);

		// ========== 页面冻结/恢复事件（部分浏览器支持） ==========
		// freeze：浏览器将暂停页面执行（如标签页长期未访问）
		// resume：页面从冻结状态恢复
		if ('onfreeze' in document) {
			document.addEventListener('freeze', () => {
				console.log('[WS] 页面冻结');
				this.lastHeartbeatResponseTime = Date.now(); // 记录冻结时间，避免误判超时
			});

			document.addEventListener('resume', () => {
				console.log('[WS] 页面恢复');
				this._handleVisibilityRestore(); // 恢复时探测连接
			});
		}

		// ========== 网络状态变化监听 ==========
		// online：网络从离线恢复
		window.addEventListener('online', () => {
			console.log('[WS] 网络恢复在线');
			// 网络恢复且连接断开时自动重连
			if (!this.connected && !this.isConnecting && !this.isManualClose) {
				this.connect();
			}
		});

		// offline：网络断开
		window.addEventListener('offline', () => {
			console.log('[WS] 网络离线');
			this._stopHeartbeat(); // 停止心跳，避免无效发送
		});
	}

	/**
	 * 处理页面恢复可见时的连接检测
	 *
	 * 关键问题：
	 * 后台期间 onclose 事件可能被浏览器节流未触发，
	 * 导致 this.connected 仍为 true 但实际已断开，因此需要主动探测
	 *
	 * 处理流程：
	 * 1. 停止后台 Worker 心跳
	 * 2. 主动检查 ws.readyState 判断真实连接状态
	 * 3. 如果已断开：修正状态标志并重连
	 * 4. 如果连接存在：发送验证心跳确认是否真的可用
	 */
	_handleVisibilityRestore() {
		// 停止后台 Worker 心跳，恢复正常心跳
		this._stopWorkerHeartbeat();

		// 清除之前的重连定时器，避免冲突
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}

		// 主动探测真实连接状态（不依赖 this.connected 标志）
		// readyState 值：0=CONNECTING, 1=OPEN, 2=CLOSING, 3=CLOSED
		const isActuallyConnected = this.ws && this.ws.readyState === WebSocket.OPEN;

		if (!isActuallyConnected) {
			// ========== 情况1：实际已断开 ==========
			console.log('[WS] 页面恢复后检测到连接已断开（readyState:', this.ws ? this.ws.readyState : 'null', '），尝试重连');
			this.connected = false; // 修正状态标志
			this._emitStatusChange(false); // 通知监听者连接已断开

			// 短暂延迟后重连，给浏览器恢复时间
			setTimeout(() => {
				if (!this.connected && !this.isConnecting && !this.isManualClose) {
					// 重置重连计数，给予完整的重连机会
					if (this.reconnectCount >= this.maxReconnectCount) {
						this.reconnectCount = 0;
					}
					this.connect();
				}
			}, CONFIG.VISIBILITY_RECONNECT_DELAY);
		} else {
			// ========== 情况2：连接存在，发送验证心跳确认 ==========
			console.log('[WS] 页面恢复，连接存在，发送验证心跳确认');
			this.lastHeartbeatResponseTime = Date.now(); // 重置心跳响应时间
			this._startHeartbeat(); // 恢复正常心跳

			// 发送验证心跳
			const sent = this.send(CONFIG.HEARTBEAT_MESSAGE);
			if (!sent) {
				// 发送失败，说明连接已失效（虽然 readyState 显示 OPEN，但实际已断开）
				console.warn('[WS] 验证心跳发送失败，连接已失效，重连');
				this.connected = false;
				this._emitStatusChange(false);

				// 清理失效的连接
				if (this.ws) {
					this.ws.onclose = null; // 移除 onclose，避免触发额外重连
					this.ws.close();
					this.ws = null;
				}
				this._stopHeartbeat();

				// 延迟后重连
				setTimeout(() => {
					if (!this.isManualClose) {
						if (this.reconnectCount >= this.maxReconnectCount) {
							this.reconnectCount = 0;
						}
						this.connect();
					}
				}, CONFIG.VISIBILITY_RECONNECT_DELAY);
			} else {
				// 心跳发送成功，重置超时检测
				this._resetHeartbeatTimeout();
			}
		}
	}

	// ─── 连接管理 ────────────────────────────────────────────────────────────

	/**
	 * 发起 WebSocket 连接
	 *
	 * 前置检查：
	 * 1. 浏览器是否支持 WebSocket
	 * 2. 网络是否在线
	 * 3. 是否已连接或正在连接中（防重入）
	 *
	 * 连接流程：
	 * 1. 创建 WebSocket 实例
	 * 2. 绑定 onopen/onmessage/onclose/onerror 事件
	 * 3. 等待 onopen 事件确认连接成功
	 */
	connect() {
		// 检查浏览器是否支持 WebSocket
		if (!window.WebSocket) {
			console.error('[WS] 浏览器不支持 WebSocket');
			return;
		}

		// 检查网络状态
		if (!navigator.onLine) {
			console.log('[WS] 网络离线，延迟连接');
			return;
		}

		// 防重入：已连接或正在连接中则跳过
		if (this.connected || this.isConnecting) {
			console.log('[WS] 已连接或正在连接中，跳过重复连接');
			return;
		}

		// 清除之前的重连定时器
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}

		try {
			this.isConnecting = true; // 标记正在连接

			// 创建 WebSocket 实例
			this.ws = new WebSocket(this.url);

			// 绑定事件处理器（使用 bind 确保 this 指向当前实例）
			this.ws.onopen = this._handleOpen.bind(this); // 连接打开
			this.ws.onmessage = this._handleMessage.bind(this); // 收到消息
			this.ws.onclose = this._handleClose.bind(this); // 连接关闭
			this.ws.onerror = this._handleError.bind(this); // 连接错误
		} catch (error) {
			console.error('[WS] 创建连接异常:', error);
			this.isConnecting = false;
			this._scheduleReconnect();
		}
	}

	/**
	 * 主动关闭连接（不触发重连）
	 *
	 * 使用场景：
	 * - 用户主动退出登录
	 * - 页面销毁时清理资源
	 * - 手动切换到其他服务器
	 */
	close() {
		console.log('[WS] 主动关闭连接');
		this.isManualClose = true; // 标记手动关闭，阻止自动重连
		this.isConnecting = false; // 重置连接中标记
		this._stopHeartbeat(); // 停止前台心跳
		this._stopWorkerHeartbeat(); // 停止后台 Worker 心跳
		this._clearHeartbeatTimeout(); // 清除心跳超时检测

		// 清除重连定时器
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}

		// 关闭底层连接
		if (this.ws) {
			this.ws.onclose = null; // 移除事件监听，避免触发重连
			this.ws.onerror = null;
			this.ws.close();
			this.ws = null;
		}

		this.connected = false; // 标记断开
		this.reconnectCount = 0; // 重置重连计数
	}

	/**
	 * 销毁实例，清理所有资源
	 *
	 * 使用场景：
	 * - 应用退出时
	 * - 模块卸载时
	 * - 单例重置时
	 */
	destroy() {
		this.close(); // 关闭连接

		// 清理页面可见性监听
		if (this.visibilityChangeHandler) {
			document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
			this.visibilityChangeHandler = null;
		}

		// 清理所有消息处理器
		this.messageHandlers.clear();

		// 清理所有状态监听器
		this.statusChangeHandlers.clear();
	}

	/**
	 * 更新 WebSocket URL 并重新连接
	 *
	 * 使用场景：
	 * - token 更换后需要重新连接
	 * - 切换到备用服务器
	 *
	 * @param {string} newUrl 新的连接地址
	 * @param {boolean} autoConnect 是否自动重连，默认 true
	 */
	updateUrl(newUrl, autoConnect = true) {
		if (!newUrl) {
			console.error('[WS] URL 不能为空');
			return;
		}

		console.log('[WS] 更新 URL:', newUrl);
		this.url = newUrl;

		if (autoConnect) {
			this.reconnect(); // 断开当前连接并用新 URL 重连
		}
	}

	/**
	 * 重新连接（先关闭再连接）
	 *
	 * 与自动重连的区别：
	 * - 手动重连会重置重连计数
	 * - 手动重连不受 isManualClose 标记影响
	 */
	reconnect() {
		console.log('[WS] 手动重新连接');
		const oldManualClose = this.isManualClose; // 暂存手动关闭标记
		this.isManualClose = false; // 临时改为 false 以允许重连

		// 关闭现有连接（移除事件监听避免触发自动重连）
		if (this.ws) {
			this.ws.onclose = null;
			this.ws.onerror = null;
			this.ws.close();
			this.ws = null;
		}
		this.connected = false;
		this.isConnecting = false;

		// 重置重连计数
		this.reconnectCount = 0;

		// 开始新连接
		this.connect();

		// 恢复原状态
		this.isManualClose = oldManualClose;
	}

	// ─── 消息收发 ────────────────────────────────────────────────────────────

	/**
	 * 发送消息到服务器
	 *
	 * @param {Object|string} data 要发送的数据
	 *   - Object 类型：自动 JSON.stringify 后发送
	 *   - string 类型：直接发送
	 * @returns {boolean} 是否成功发送（连接正常返回 true）
	 */
	send(data) {
		// 连接检查：必须已连接且 readyState 为 OPEN
		if (!this.connected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
			console.warn('[WS] 未连接，无法发送消息');
			return false;
		}

		try {
			// Object 类型转为 JSON 字符串，string 类型直接发送
			this.ws.send(typeof data === 'object' ? JSON.stringify(data) : data);
			return true;
		} catch (error) {
			console.error('[WS] 发送失败:', error);
			return false;
		}
	}

	/**
	 * 注册消息处理器
	 *
	 * 当收到指定 type 的消息时，会调用注册的 handler
	 * 支持同一类型注册多个处理器，消息到来时会依次调用
	 *
	 * @param {string} type 消息类型（如 'chat_record'、'get_session_info'）
	 *                      使用 'default' 表示处理所有未匹配的消息
	 * @param {Function} handler 处理函数，接收解析后的消息对象
	 */
	on(type, handler) {
		if (!this.messageHandlers.has(type)) {
			// 首次注册该类型，创建 Set 存储
			this.messageHandlers.set(type, new Set());
		}
		this.messageHandlers.get(type).add(handler);
	}

	/**
	 * 移除消息处理器
	 *
	 * @param {string} type 消息类型
	 * @param {Function} handler 要移除的处理函数（必须是注册时的同一个引用）
	 */
	off(type, handler) {
		const handlers = this.messageHandlers.get(type);
		if (handlers) {
			handlers.delete(handler);
			// 如果该类型已无处理器，删除整个条目
			if (handlers.size === 0) this.messageHandlers.delete(type);
		}
	}

	/**
	 * 注册连接状态变化监听器
	 *
	 * @param {Function} handler 回调函数，参数 isConnected (boolean)
	 * @returns {Function} 返回取消监听的函数，调用即可注销
	 */
	onStatusChange(handler) {
		this.statusChangeHandlers.add(handler);
		// 返回取消监听函数，方便在组件销毁时清理
		return () => this.statusChangeHandlers.delete(handler);
	}

	// ─── 私有方法 ────────────────────────────────────────────────────────────

	/**
	 * WebSocket 连接成功回调
	 *
	 * 触发时机：WebSocket 握手完成，连接已建立
	 *
	 * 处理内容：
	 * 1. 更新连接状态标记
	 * 2. 重置重连计数
	 * 3. 启动心跳机制
	 * 4. 通知所有监听者
	 */
	_handleOpen() {
		console.log('[WS] 连接成功');
		this.connected = true; // 标记已连接
		this.isConnecting = false; // 清除连接中标记
		this.reconnectCount = 0; // 重置重连计数
		this.isManualClose = false; // 重置手动关闭标记
		this.lastHeartbeatResponseTime = Date.now(); // 记录心跳响应时间
		this._startHeartbeat(); // 启动心跳机制
		this._emitStatusChange(true); // 通知所有监听者：已连接
	}

	/**
	 * WebSocket 连接关闭回调
	 *
	 * 触发时机：
	 * - 服务器主动断开
	 * - 网络中断
	 * - 客户端主动关闭（已移除监听，不会触发）
	 *
	 * @param {CloseEvent} event 关闭事件，包含 code（关闭码）和 reason（关闭原因）
	 */
	_handleClose(event) {
		console.log('[WS] 连接关闭, code:', event.code, ', reason:', event.reason);
		this.connected = false; // 标记断开
		this.isConnecting = false; // 清除连接中标记
		this._stopHeartbeat(); // 停止心跳
		this._clearHeartbeatTimeout(); // 清除心跳超时检测
		this._emitStatusChange(false); // 通知状态变化

		// 非主动关闭才自动重连
		if (!this.isManualClose) {
			this._scheduleReconnect();
		}
	}

	/**
	 * WebSocket 连接错误回调
	 *
	 * 触发时机：
	 * - 连接失败
	 * - 握手被拒
	 * - 证书问题
	 *
	 * 注意：onerror 之后通常会触发 onclose，两个事件都会尝试重连，
	 * 但由于 _scheduleReconnect 中有防重复机制，不会导致多次重连
	 *
	 * @param {Event} error 错误事件
	 */
	_handleError(error) {
		console.error('[WS] 连接错误:', error);
		this.connected = false; // 标记断开
		this.isConnecting = false; // 清除连接中标记
		this._stopHeartbeat(); // 停止心跳
		this._clearHeartbeatTimeout(); // 清除心跳超时检测
		this._emitStatusChange(false); // 通知状态变化

		// 错误后也尝试重连
		if (!this.isManualClose) {
			this._scheduleReconnect();
		}
	}

	/**
	 * 收到 WebSocket 消息回调
	 *
	 * 处理流程：
	 * 1. 解析 JSON 数据
	 * 2. 识别心跳响应（pong）并处理
	 * 3. 根据消息 type 分发到对应的处理器
	 * 4. 未找到特定处理器时，使用 'default' 处理器兜底
	 *
	 * @param {MessageEvent} event 消息事件，包含 data 字段
	 */
	_handleMessage(event) {
		try {
			// 解析 JSON 数据
			const message = JSON.parse(event.data);

			// ========== 识别心跳响应 ==========
			// 支持多种 pong 格式，兼容不同服务端实现
			if (message.a === 'pong' || message.type === 'pong' || message.msg === 'pong') {
				this.lastHeartbeatResponseTime = Date.now(); // 更新心跳响应时间
				this._resetHeartbeatTimeout(); // 重置超时检测
				return; // 心跳响应不需要分发给业务处理器
			}

			// ========== 消息分发 ==========
			const type = message.type || 'default';
			const handlers = this.messageHandlers.get(type);

			if (handlers && handlers.size > 0) {
				// 找到特定类型的处理器，依次调用
				handlers.forEach((handler) => {
					try {
						handler(message);
					} catch (e) {
						console.error('[WS] 消息处理异常:', e);
					}
				});
			} else {
				// 回退：使用 'default' 类型的处理器
				const defaultHandlers = this.messageHandlers.get('default');
				if (defaultHandlers && defaultHandlers.size > 0) {
					defaultHandlers.forEach((handler) => {
						try {
							handler(message);
						} catch (e) {
							console.error('[WS] 消息处理异常:', e);
						}
					});
				}
				// 无任何处理器，消息被丢弃
			}
		} catch (error) {
			console.error('[WS] 消息解析失败:', error);
		}
	}

	/**
	 * 调度自动重连
	 *
	 * 重连策略：
	 * 1. 网络离线时不重连
	 * 2. 延迟时间随重连次数递增（第1次3秒，第2次6秒，第3次9秒，之后保持9秒）
	 * 3. 超过最大重连次数（10次）后，等待30秒重置计数再尝试
	 * 4. 避免页面长时间后台导致重连次数耗尽后永久断连
	 */
	_scheduleReconnect() {
		// 检查网络状态
		if (!navigator.onLine) {
			console.log('[WS] 网络离线，暂停重连');
			return; // 网络离线，等网络恢复后再连
		}

		// 超过最大重连次数
		if (this.reconnectCount >= this.maxReconnectCount) {
			console.error('[WS] 已达最大重连次数，停止重连');
			// 不再永久放弃：等待较长时间后重置计数，给页面恢复/网络恢复留出机会
			// 这样可避免长时间最小化后重连次数耗尽导致永久断连
			this.reconnectTimer = setTimeout(() => {
				console.log('[WS] 重置重连计数，重新尝试连接');
				this.reconnectCount = 0;
				this.connect();
			}, this.reconnectDelay * 10); // 30秒后重置重试
			return;
		}

		// 重连计数 +1
		this.reconnectCount++;

		// 计算延迟时间：基础延迟 × min(重连次数, 3)，最多 3 倍（9秒）
		const delay = this.reconnectDelay * Math.min(this.reconnectCount, 3);
		console.log(`[WS] 第 ${this.reconnectCount}/${this.maxReconnectCount} 次重连，${delay}ms 后重试`);

		// 设置重连定时器
		this.reconnectTimer = setTimeout(() => this.connect(), delay);
	}

	// ─── 心跳管理（增强版） ──────────────────────────────────────────────────

	/**
	 * 启动前台心跳定时器
	 *
	 * 心跳机制：
	 * - 每隔 HEARTBEAT_DELAY (28秒) 发送一次心跳包
	 * - 发送后启动超时检测，超时未收到 pong 则判定断开
	 *
	 * 注意：此方法只在页面可见时使用，页面隐藏时应切换到 Worker 心跳
	 */
	_startHeartbeat() {
		this._stopHeartbeat(); // 先清除已有定时器
		this.lastHeartbeatResponseTime = Date.now(); // 记录心跳响应时间

		// 设置心跳发送定时器
		this.heartbeatTimer = setInterval(() => {
			// 连接正常才发送心跳
			if (this.connected && this.ws && this.ws.readyState === WebSocket.OPEN) {
				this.send(CONFIG.HEARTBEAT_MESSAGE); // 发送心跳包
				this._startHeartbeatTimeout(); // 启动超时检测
			}
		}, CONFIG.HEARTBEAT_DELAY);
	}

	/**
	 * 停止前台心跳定时器
	 *
	 * 注意：不在此处停止 Worker 心跳，避免后台模式下 Worker 被误杀
	 * Worker 心跳由 _startWorkerHeartbeat / _stopWorkerHeartbeat 独立管理
	 */
	_stopHeartbeat() {
		if (this.heartbeatTimer) {
			clearInterval(this.heartbeatTimer);
			this.heartbeatTimer = null;
		}
	}

	/**
	 * 启动 Web Worker 心跳（后台不被浏览器节流）
	 *
	 * 背景知识：
	 * 浏览器会对后台标签页的 setInterval/setTimeout 进行节流，
	 * 通常降至 1 次/分钟甚至更低，导致心跳无法按时发送，连接被服务端断开。
	 * Web Worker 中的定时器不受页面可见性影响，可保持稳定的心跳频率。
	 *
	 * 实现方式：
	 * 1. 创建内联 Web Worker（通过 Blob URL）
	 * 2. Worker 内部使用 setInterval 定时发送心跳消息
	 * 3. 主线程收到 Worker 消息后，通过 WebSocket 发送实际心跳
	 */
	_startWorkerHeartbeat() {
		this._stopWorkerHeartbeat(); // 先停止已有 Worker
		if (this.heartbeatWorker) return; // 防重复

		// ========== 创建内联 Web Worker 代码 ==========
		const workerCode = `
			let timer = null;
			self.onmessage = function(e) {
				if (e.data.cmd === 'start') {
					// 启动定时器
					const interval = e.data.interval || 1000;
					if (timer) clearInterval(timer);
					timer = setInterval(function() {
						self.postMessage({ cmd: 'heartbeat' }); // 定时发送心跳消息
					}, interval);
				} else if (e.data.cmd === 'stop') {
					// 停止定时器
					if (timer) {
						clearInterval(timer);
						timer = null;
					}
				}
			};
		`;

		try {
			// 创建 Blob URL 并实例化 Worker
			const blob = new Blob([workerCode], { type: 'application/javascript' });
			this.workerUrl = URL.createObjectURL(blob);
			this.heartbeatWorker = new Worker(this.workerUrl);

			// 监听 Worker 消息
			this.heartbeatWorker.onmessage = (e) => {
				if (e.data.cmd === 'heartbeat' && this.connected && this.ws && this.ws.readyState === WebSocket.OPEN) {
					console.log('[WS] Worker 心跳发送（后台保活）');
					this.send(CONFIG.HEARTBEAT_MESSAGE); // 通过 WebSocket 发送实际心跳
					this._startHeartbeatTimeout(); // 后台使用更宽容的超时时间
				}
			};

			// 启动 Worker 定时器
			this.heartbeatWorker.postMessage({
				cmd: 'start',
				interval: CONFIG.BACKGROUND_HEARTBEAT_DELAY // 10 秒间隔
			});
			console.log('[WS] Web Worker 心跳已启动，间隔:', CONFIG.BACKGROUND_HEARTBEAT_DELAY, 'ms');
		} catch (error) {
			console.error('[WS] Web Worker 心跳启动失败，降级为普通心跳:', error);
			// 降级：使用普通心跳（虽然会被节流，但聊胜于无）
			this._startHeartbeat();
		}
	}

	/**
	 * 停止 Web Worker 心跳
	 *
	 * 清理内容：
	 * 1. 停止 Worker 中的定时器
	 * 2. 终止 Worker 实例
	 * 3. 释放 Blob URL（避免内存泄漏）
	 */
	_stopWorkerHeartbeat() {
		if (this.heartbeatWorker) {
			this.heartbeatWorker.postMessage({ cmd: 'stop' }); // 停止 Worker 中的定时器
			this.heartbeatWorker.terminate(); // 终止 Worker
			this.heartbeatWorker = null;
			console.log('[WS] Web Worker 心跳已停止');
		}

		// 释放 Blob URL，避免内存泄漏
		if (this.workerUrl) {
			URL.revokeObjectURL(this.workerUrl);
			this.workerUrl = null;
		}
	}

	/**
	 * 启动心跳超时检测
	 *
	 * 超时判定：
	 * - 前台：60 秒未收到 pong 则判定断开
	 * - 后台：120 秒未收到 pong 则判定断开（更宽容，避免浏览器节流导致误断）
	 *
	 * 超时后处理：
	 * 1. 标记连接断开
	 * 2. 关闭当前连接
	 * 3. 触发自动重连
	 */
	_startHeartbeatTimeout() {
		this._clearHeartbeatTimeout(); // 先清除已有超时检测

		// 根据前后台状态选择超时时间
		const timeout = this.isBackground ? CONFIG.BACKGROUND_HEARTBEAT_TIMEOUT : CONFIG.HEARTBEAT_TIMEOUT;

		this.heartbeatTimeoutTimer = setTimeout(() => {
			const timeSinceLastResponse = Date.now() - this.lastHeartbeatResponseTime;
			if (timeSinceLastResponse >= timeout) {
				// ========== 超时：判定连接断开 ==========
				console.warn('[WS] 心跳超时，超过', timeSinceLastResponse, 'ms未收到响应，主动断开重连');
				this.connected = false;
				this._emitStatusChange(false);

				// 主动关闭当前连接，触发重连
				if (this.ws) {
					this.ws.onclose = null; // 移除 onclose，避免触发额外重连
					this.ws.close();
					this.ws = null;
				}
				this._stopHeartbeat();
				this._scheduleReconnect();
			} else {
				// 未超时：重新设置定时器
				this._startHeartbeatTimeout();
			}
		}, timeout);
	}

	/**
	 * 重置心跳超时检测（收到心跳响应时调用）
	 *
	 * 收到 pong 后：
	 * 1. 清除现有的超时定时器
	 * 2. 如果连接仍然存在，重新启动超时检测
	 */
	_resetHeartbeatTimeout() {
		this._clearHeartbeatTimeout();
		if (this.connected) {
			this._startHeartbeatTimeout();
		}
	}

	/**
	 * 清除心跳超时检测定时器
	 */
	_clearHeartbeatTimeout() {
		if (this.heartbeatTimeoutTimer) {
			clearTimeout(this.heartbeatTimeoutTimer);
			this.heartbeatTimeoutTimer = null;
		}
	}

	/**
	 * 通知所有状态变化监听器
	 *
	 * @param {boolean} isConnected 当前连接状态（true=已连接，false=已断开）
	 */
	_emitStatusChange(isConnected) {
		this.statusChangeHandlers.forEach((handler) => {
			try {
				handler(isConnected);
			} catch (e) {
				console.error('[WS] 状态处理异常:', e);
			}
		});
	}
}

// ─── 单例管理 ────────────────────────────────────────────────────────────────

/**
 * 模块级单例存储
 *
 * key: 模块名称（如 'default'、'chat' 等）
 * value: SimpleWebSocket 实例
 *
 * 设计目的：
 * - 全局只维护一个 WebSocket 连接（同一 moduleName）
 * - 所有页面/组件共享同一连接，避免重复建连
 * - 支持多模块（不同业务线可创建独立连接）
 */
const wsInstances = {};

/**
 * 创建或获取 WebSocket 单例
 *
 * 如果指定 moduleName 的实例已存在，直接返回；否则创建新实例
 *
 * @param {string} url WebSocket 连接地址（仅首次调用有效）
 * @param {string} moduleName 模块名称，默认 'default'
 * @returns {SimpleWebSocket} WebSocket 实例
 */
export function createWebSocket(url, moduleName = 'default') {
	if (!wsInstances[moduleName] && url) {
		wsInstances[moduleName] = new SimpleWebSocket(url);
	}
	return wsInstances[moduleName];
}

/**
 * 获取当前 WebSocket 实例（不会创建新的）
 *
 * @param {string} moduleName 模块名称，默认 'default'
 * @returns {SimpleWebSocket|null} WebSocket 实例或 null
 */
export function getWebSocketInstance(moduleName = 'default') {
	return wsInstances[moduleName] || null;
}

/**
 * 重置 WebSocket 单例
 *
 * @param {boolean} closeConnection 是否关闭连接，默认 true
 * @param {string} moduleName 模块名称，默认 'default'
 */
export function resetWebSocket(closeConnection = true, moduleName = 'default') {
	if (wsInstances[moduleName]) {
		if (closeConnection) {
			wsInstances[moduleName].destroy(); // 使用 destroy 清理所有资源
		}
		wsInstances[moduleName] = null; // 清除单例引用
	}
}

/**
 * 更新 WebSocket 连接
 *
 * 如果实例已存在且 URL 相同且已连接，则跳过；
 * 否则更新 URL 并（可选）重新连接
 *
 * @param {string} newUrl 新的 WebSocket URL
 * @param {boolean} autoConnect 是否自动连接，默认 true
 * @param {string} moduleName 模块名称，默认 'default'
 * @returns {SimpleWebSocket|null} WebSocket 实例
 */
export function updateWebSocketConnection(newUrl, autoConnect = true, moduleName = 'default') {
	if (!newUrl) {
		console.error('[WS] updateWebSocketConnection: URL 不能为空');
		return null;
	}

	if (wsInstances[moduleName]) {
		// 实例已存在
		const instance = wsInstances[moduleName];
		if (instance.url === newUrl && instance.connected) {
			// URL 相同且已连接，无需操作
			console.log('[WS] 已有相同 URL 的连接，跳过重新连接');
			return instance;
		}
		// URL 不同或未连接，更新 URL
		instance.updateUrl(newUrl, autoConnect);
	} else {
		// 实例不存在，创建新实例
		wsInstances[moduleName] = new SimpleWebSocket(newUrl);
		if (autoConnect) {
			wsInstances[moduleName].connect();
		}
	}

	return wsInstances[moduleName];
}

// 默认导出 SimpleWebSocket 类，方便直接引用
export default SimpleWebSocket;